import { Link, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import logger from "@/lib/logger";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { LogOut, LayoutDashboard, Calendar as CalIcon, Wallet, ShieldCheck, History, FileText, IdCard } from "lucide-react";
import LoginNotifications from "@/components/LoginNotifications";
import TermsReacceptDialog from "@/components/TermsReacceptDialog";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";

export default function Layout({ children }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const isAdmin = user && (user.role === "admin" || user.role === "super_admin");
    const [notifTrigger, setNotifTrigger] = useState(0);
    const [termsOK, setTermsOK] = useState(null); // null=unknown, true=in-sync, false=needs reaccept
    const [memberId, setMemberId] = useState("");

    // Fetch the user's Member ID once on login so the header pill renders immediately.
    useEffect(() => {
        if (!user) {
            setMemberId("");
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const r = await api.get("/me/member-id");
                if (!cancelled) setMemberId(r.data.member_id || "");
            } catch (e) {
                if (!cancelled) logger.warn("Layout", "could not load Member ID:", e?.message || e);
            }
        })();
        return () => { cancelled = true; };
    }, [user]);

    // Check terms version before deciding to fire notifications
    useEffect(() => {
        if (!user) return;
        let cancelled = false;
        (async () => {
            try {
                const r = await api.get("/terms/version");
                if (cancelled) return;
                setTermsOK(user.terms_accepted_version === r.data.version);
            } catch (_) {
                if (!cancelled) setTermsOK(true); // fail-open so app remains usable
            }
        })();
        return () => { cancelled = true; };
    }, [user]);

    useEffect(() => {
        if (!user || termsOK !== true) return;
        const key = `login_notif_seen_${user.id}`;
        if (!sessionStorage.getItem(key)) {
            sessionStorage.setItem(key, "1");
            setNotifTrigger((t) => t + 1);
        }
    }, [user, termsOK]);

    const navItem = (to, label, Icon, testid) => (
        <NavLink
            to={to}
            data-testid={testid}
            className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors ${
                    isActive
                        ? "bg-brand text-white"
                        : "text-slate-700 hover:bg-slate-100 hover:text-slate-900"
                }`
            }
        >
            <Icon className="h-4 w-4" strokeWidth={1.75} />
            <span>{label}</span>
        </NavLink>
    );

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200/60">
                <div className="px-6 md:px-12 lg:px-16 h-16 flex items-center justify-between">
                    <Link to="/dashboard" className="flex items-center gap-2.5 group" data-testid="logo-home-link">
                        <div className="h-8 w-8 bg-brand text-white grid place-items-center font-heading font-bold tracking-tight rounded-sm">
                            A
                        </div>
                        <div className="leading-tight">
                            <div className="font-heading font-bold text-slate-900 text-base">Allied Pod</div>
                            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-mono">Barbados</div>
                        </div>
                    </Link>
                    <nav className="hidden md:flex items-center gap-1">
                        {navItem("/dashboard", "Dashboard", LayoutDashboard, "nav-dashboard")}
                        {navItem("/calendar", "Book a Pod", CalIcon, "nav-calendar")}
                        {navItem("/bookings", "My Bookings", History, "nav-bookings")}
                        {navItem("/credits", "Credits", Wallet, "nav-credits")}
                        {isAdmin && navItem("/admin", "Admin", ShieldCheck, "nav-admin")}
                        {navItem("/terms", "Terms", FileText, "nav-terms")}
                    </nav>
                    <div className="flex items-center gap-2 md:gap-3">
                        {memberId && (
                            <button
                                type="button"
                                onClick={() => navigate("/credits")}
                                data-testid="header-member-id"
                                title={`Your Member ID — tap to view & copy on the Credits page (${memberId})`}
                                className="hidden sm:inline-flex items-center gap-1.5 bg-slate-50 hover:bg-brand/5 border border-slate-200 hover:border-brand/40 transition-colors rounded-full pl-2.5 pr-3 py-1.5 group"
                            >
                                <IdCard className="h-3.5 w-3.5 text-brand shrink-0" strokeWidth={2} />
                                <span className="font-mono text-[11px] sm:text-xs tracking-[0.18em] font-bold text-slate-900 group-hover:text-brand transition-colors">
                                    {memberId}
                                </span>
                            </button>
                        )}
                        <div className="hidden sm:flex flex-col items-end leading-tight">
                            <span className="text-sm font-medium text-slate-900">{user?.name}</span>
                            <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-mono">{user?.role?.replace("_", " ")}</span>
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            data-testid="logout-button"
                            onClick={async () => {
                                await logout();
                                navigate("/");
                            }}
                            className="rounded-md"
                        >
                            <LogOut className="h-4 w-4 mr-1.5" strokeWidth={1.75} />
                            <span className="hidden sm:inline">Sign out</span>
                        </Button>
                    </div>
                </div>
                {/* Mobile-only Member ID strip (visible below logo on phones <= xs) */}
                {memberId && (
                    <button
                        type="button"
                        onClick={() => navigate("/credits")}
                        data-testid="header-member-id-mobile"
                        className="sm:hidden mx-4 mb-2 inline-flex items-center justify-between gap-2 bg-slate-50 hover:bg-brand/5 border border-slate-200 hover:border-brand/40 rounded-md px-3 py-2 w-[calc(100%-2rem)] transition-colors group"
                    >
                        <span className="flex items-center gap-2">
                            <IdCard className="h-3.5 w-3.5 text-brand" strokeWidth={2} />
                            <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-mono">Member ID</span>
                        </span>
                        <span className="font-mono text-sm tracking-[0.18em] font-bold text-slate-900 group-hover:text-brand transition-colors">
                            {memberId}
                        </span>
                    </button>
                )}
                {/* Mobile nav */}
                <div className="md:hidden px-4 pb-3 flex gap-1 overflow-x-auto">
                    {navItem("/dashboard", "Dashboard", LayoutDashboard, "nav-dashboard-mobile")}
                    {navItem("/calendar", "Book", CalIcon, "nav-calendar-mobile")}
                    {navItem("/bookings", "Bookings", History, "nav-bookings-mobile")}
                    {navItem("/credits", "Credits", Wallet, "nav-credits-mobile")}
                    {isAdmin && navItem("/admin", "Admin", ShieldCheck, "nav-admin-mobile")}
                    {navItem("/terms", "Terms", FileText, "nav-terms-mobile")}
                </div>
            </header>
            <main className="flex-1 page-enter">{children}</main>
            <LoginNotifications trigger={notifTrigger} />
            <TermsReacceptDialog />
            <PWAInstallPrompt />
            <footer className="border-t border-slate-200/60 px-6 md:px-12 lg:px-16 py-6 text-xs text-slate-500 flex flex-col sm:flex-row items-center justify-between gap-2">
                <div>© {new Date().getFullYear()} Allied Consulting (Barbados). All rights reserved.</div>
                <div className="font-mono uppercase tracking-[0.16em] text-[10px]">Flexible Office Pod Booking</div>
            </footer>
        </div>
    );
}
